import React from 'react';
import I18n from 'i18n-js';

import { RCButton, RCSkeleton, useMediaQuery } from '@reverbdotcom/cadence/components';
import { ImagesIcon } from '@reverbdotcom/cadence/icons/react';

import LightboxImageItem from './lightbox_image_item';

const MAX_IMAGES_IN_GRID = 7;
const MAX_IMAGES_IN_GRID_MOBILE = 1;
const BLANK_ITEM_URL = '';

interface IGridItemProps {
  imageUrl: string;
  imageIndex: number;
  openLightbox: (event, imageIndex) => void;
  imageAlt?: string;
}

function GridItem({
  imageUrl,
  imageIndex,
  imageAlt,
  openLightbox,
}: IGridItemProps) {
  const isBlankItem = imageUrl == BLANK_ITEM_URL;

  if (isBlankItem) return <RCSkeleton shape="square" display="block" />;

  return (
    <LightboxImageItem
      urlOrSlug={imageUrl}
      index={imageIndex}
      alt={imageAlt}
      onClick={e => openLightbox(e, imageIndex)}
      imageOverlay="offwhiteWithHover"
    />
  );
}

interface IShowMoreButtonProps {
  openLightbox: (e, imageIndex) => void;
}

function ShowMoreButton({ openLightbox }: IShowMoreButtonProps) {
  const isTablet = useMediaQuery('tablet');
  const firstImageIndex = 0;

  function handleClick(e) {
    openLightbox(e, firstImageIndex);
  }

  return (
    <div className="lightbox-image-grid__item__button">
      <RCButton
        variant="light"
        onClick={handleClick}
        icon={<ImagesIcon />}
        iconPosition={isTablet ? 'iconOnly' : undefined}
      >
        {I18n.t('commons.lightboxImage.showAllPhotos')}
      </RCButton>
    </div>
  );
}

interface ICustomGridButton {
  imageGridButton: React.ReactNode;
}

function CustomGridButton({ imageGridButton }: ICustomGridButton) {
  return (
    <div className="lightbox-image-grid__item__button">
      {imageGridButton}
    </div>
  );
}

interface IProps {
  imageUrls: string[];
  openLightbox: (e, imageIndex) => void;
  imageGridButton?: React.ReactNode;
  imageAlts?: string[];
}

export default function LightboxImageGrid({
  imageUrls,
  imageAlts = [],
  openLightbox,
  imageGridButton,
}: IProps) {
  const isMobile = useMediaQuery('mobile');

  if (!imageUrls.length) return null;

  const maxImagesInGrid = isMobile ? MAX_IMAGES_IN_GRID_MOBILE : MAX_IMAGES_IN_GRID;
  const lastImageIndex = maxImagesInGrid - 1;

  const blankSquareCount = maxImagesInGrid - imageUrls.length;
  const blankUrls = blankSquareCount > 0 ? new Array(blankSquareCount).fill(BLANK_ITEM_URL) : [];

  const urls = imageUrls.concat(blankUrls);

  function renderGridButton(imageIndex) {
    if (lastImageIndex != imageIndex) return null;

    return imageGridButton ?
      <CustomGridButton imageGridButton={imageGridButton} /> :
      <ShowMoreButton openLightbox={openLightbox} />;
  }

  return (
    <div className="lightbox-image-grid">
      {urls.map((imageUrl, imageIndex) =>
        <div className="lightbox-image-grid__item" key={imageIndex}>
          <GridItem
            imageUrl={imageUrl}
            imageIndex={imageIndex}
            imageAlt={imageAlts[imageIndex]}
            openLightbox={openLightbox}
          />
          {renderGridButton(imageIndex)}
        </div>,
      )}
    </div>
  );
}
